/**
 * `MarketLifecycle` → market-row `status` mapper.
 *
 * The v0 NATS taxonomy publishes leader-change / trading-started /
 * graduation / backing-started on `morfi.market.{m}.lifecycle.v0`
 * (proto `morfi.v1.MarketLifecycle`). Resolution is no longer received
 * on this subject — it arrives on `morfi.market.{m}.resolution.v0`
 * (proto `morfi.v1.MarketResolved`) handled by `mapResolvedStatus`.
 *
 * The wire carries only a `kind` enum + optional `leader` — NOT a
 * `status` string — so a declarative `set: { status: kind }` recipe
 * would write the raw enum number into a string column. This named
 * mapper translates the enum to the backend's market `status` vocabulary
 * instead.
 *
 * `status` value set sourced from `openapi.json` — the GET /v1/markets
 * and GET /v1/markets/{pubkey} response `data.status` schema is
 * `{ "type": "string", "enum":
 *   ["created","baking","trading","resolved","graduated"] }`.
 *
 * LifecycleKind → status mapping (conservative — only transitions with
 * an unambiguous status counterpart map; the rest return `prev`):
 *
 *   | LifecycleKind    | value | → status     | rationale                        |
 *   |------------------|-------|--------------|----------------------------------|
 *   | UNSPECIFIED      | 0     | (no change)  | not a real transition            |
 *   | BACKING_STARTED  | 1     | 'baking'     | backing/bonding-curve phase open |
 *   | TRADING_STARTED  | 2     | 'trading'    | AMM trading phase open           |
 *   | LEADER_CHANGED   | 3     | (no change)  | leader flip ≠ a status change    |
 *   | RESOLUTION       | 4     | (no change)  | no longer on lifecycle.v0;       |
 *   |                  |       |              | handled by resolution.v0 →       |
 *   |                  |       |              | mapResolvedStatus                |
 *   | GRADUATION       | 5     | 'graduated'  | graduated to AMM                 |
 *
 * `created` has no lifecycle event (it is the row's birth state, set by
 * the create broadcast / initial fetch), so nothing maps to it — that is
 * correct, not a gap. LEADER_CHANGED has no status counterpart, so it
 * returns `prev` unchanged rather than invent one.
 *
 * Graduation applies the emitted winner together with the status so the
 * pre-resolution leader cannot appear as the resolved winner.
 * Immutable: returns a NEW object only when status or winner changes;
 * otherwise the exact `prev` reference (idempotent on replay, keeps
 * structural-sharing short-circuits intact).
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

import { sdkEvents } from '../core/events.js';

/** Numeric `morfi.v1.LifecycleKind`. Mirrors the proto enum so the
 *  mapper never imports the proto package. */
const KIND_BACKING_STARTED = 1;
const KIND_TRADING_STARTED = 2;
const KIND_LEADER_CHANGED = 3;
const KIND_GRADUATION = 5;

/** Coerce the decoded `kind` to its numeric enum value. Protobuf decode
 *  yields the number; JSON test fixtures may carry the numeric string or
 *  the proto enum name. Unknown → `0` (UNSPECIFIED → no-op). */
function coerceKind(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (/^\d+$/.test(trimmed)) return Number(trimmed);
    switch (trimmed) {
      case 'BACKING_STARTED':
      case 'LIFECYCLE_KIND_BACKING_STARTED':
        return KIND_BACKING_STARTED;
      case 'TRADING_STARTED':
      case 'LIFECYCLE_KIND_TRADING_STARTED':
        return KIND_TRADING_STARTED;
      case 'LEADER_CHANGED':
      case 'LIFECYCLE_KIND_LEADER_CHANGED':
        return KIND_LEADER_CHANGED;
      case 'GRADUATION':
      case 'LIFECYCLE_KIND_GRADUATION':
        return KIND_GRADUATION;
      default:
        return 0;
    }
  }
  return 0;
}

/** The market `status` value a given LifecycleKind transitions to, or
 *  `null` when the kind has no status counterpart (no-op). */
function statusForKind(kind: number): string | null {
  switch (kind) {
    case KIND_BACKING_STARTED:
      return 'baking';
    case KIND_TRADING_STARTED:
      return 'trading';
    case KIND_GRADUATION:
      return 'graduated';
    // KIND_LEADER_CHANGED + UNSPECIFIED + anything unknown → no-op.
    default:
      return null;
  }
}

/**
 * Apply one decoded `MarketLifecycle` record to a market row's `status`.
 *
 * `prev` is the cached row (any shape carrying an optional `status`
 * string — the codegen-emitted caller passes the concrete
 * `GetMarketsRow` / `GetMarketRow`). Returns `prev` verbatim when the
 * kind has no status counterpart or the status and winner are already current.
 */
export function mapLifecycleStatus<
  T extends {
    readonly pubkey?: string;
    readonly status?: string;
    readonly winnerOutcomeIndex?: number | null;
    readonly tradingStartedAt?: string | null;
  },
>(prev: T, raw: Record<string, unknown>): T {
  const status = statusForKind(coerceKind(raw.kind));
  if (status === null) return prev;
  if (status === 'trading') {
    const tradingStartedAt = tradingStartedAtIso(raw.ts);
    if (tradingStartedAt === null) {
      sdkEvents.emit('applyEvent:error', {
        specName: 'mapLifecycleStatus',
        queryKey: ['markets', 'entity', prev.pubkey],
        error: 'market.trading_started_at_invalid',
      });
      return prev.status === status ? prev : { ...prev, status };
    }
    if (!('tradingStartedAt' in prev)) return prev.status === status ? prev : { ...prev, status };
    if (prev.status === status && prev.tradingStartedAt === tradingStartedAt) return prev;
    return { ...prev, status, tradingStartedAt };
  }
  if (status === 'graduated') {
    const winnerOutcomeIndex = raw.leader;
    if (
      typeof winnerOutcomeIndex !== 'number' ||
      !Number.isSafeInteger(winnerOutcomeIndex) ||
      winnerOutcomeIndex < 0
    ) {
      sdkEvents.emit('applyEvent:error', {
        specName: 'mapLifecycleStatus',
        queryKey: ['markets', 'entity', prev.pubkey],
        error: 'market.graduation_winner_invalid',
      });
      return prev;
    }
    if (prev.status === status && prev.winnerOutcomeIndex === winnerOutcomeIndex) return prev;
    return { ...prev, status, winnerOutcomeIndex };
  }
  if (prev.status === status) return prev;
  return { ...prev, status };
}

function tradingStartedAtIso(value: unknown): string | null {
  let unixSeconds: bigint;
  try {
    if (typeof value === 'bigint') unixSeconds = value;
    else if (typeof value === 'number' && Number.isSafeInteger(value)) unixSeconds = BigInt(value);
    else if (typeof value === 'string' && /^(0|[1-9]\d*)$/.test(value)) unixSeconds = BigInt(value);
    else return null;
  } catch {
    return null;
  }
  if (unixSeconds < 0n) return null;
  const date = new Date(Number(unixSeconds * 1000n));
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}
